import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';

import { SlideOver, SlideOverFooter } from '../../../components/Shared/SlideOver';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import MapPicker from '../../../components/Shared/MapPicker';
import type { GeocodeAddress } from '../../../utils/geocoding';

import type { FacilityFlowCoreCommonDtosAddressDto } from '../../../api/generated/models';
import type { FacilityFlowCoreModulesCustomersDtosCreateObjectRequest } from '../../../api/generated/models';
import type { ObjectDtoWithGeo, UpdateObjectRequestWithGeo } from '../../../types/objects-api';
import {
  DEFAULT_BILLING_INTERVAL,
  OBJECT_BILLING_INTERVALS,
  toBillingInterval,
  type BillingInterval,
} from '../../../types/billing';
import { OBJECT_TYPES, objectTypeLabelKey, toObjectType, type ObjectType } from '../../../types/objectType';
import { useTranslation } from '../../../i18n';

type ObjectDto = ObjectDtoWithGeo;

type GeoDraft = {
  latitude: string;
  longitude: string;
};

type ObjectEditMode = 'create' | 'edit';

interface ObjectEditSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  mode: ObjectEditMode;
  customerId?: string;
  customers?: { id?: string | null; name?: string | null }[];
  object?: ObjectDto;
  onSave: (payload: UpdateObjectRequestWithGeo | CreateObjectRequestWithGeo) => Promise<void>;
}

type CreateObjectRequestWithGeo = FacilityFlowCoreModulesCustomersDtosCreateObjectRequest & {
  latitude?: number | null;
  longitude?: number | null;
};

const parseDecimal = (raw: string): number | null => {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

export default function ObjectEditSlideOver({
  isOpen,
  onClose,
  mode,
  customerId,
  customers,
  object,
  onSave,
}: ObjectEditSlideOverProps) {
  const { t } = useTranslation(['objects', 'common']);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [status, setStatus] = useState<string>('Active');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(DEFAULT_BILLING_INTERVAL);
  const [objectType, setObjectType] = useState<ObjectType | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const showCustomerSelect = mode === 'create' && !!customers && customers.length > 0;

  const [address, setAddress] = useState<FacilityFlowCoreCommonDtosAddressDto>({
    street: '',
    streetNumber: null,
    postalCode: '',
    city: '',
    country: '',
  });

  const [externalNotes, setExternalNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const [geo, setGeo] = useState<GeoDraft>({ latitude: '', longitude: '' });
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoTouched, setGeoTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedCustomerId(customerId ?? '');

    if (mode === 'edit' && object) {
      setName(object.name || '');
      setStatus(object.status || 'Active');
      // why: quarterly invoicing of objects is retired (FAFD-27); such an object shows as monthly.
      setBillingInterval(toBillingInterval(object.billingInterval) === 'yearly' ? 'yearly' : 'monthly');
      setObjectType(toObjectType(object.type));
      setAddress({
        street: object.address?.street ?? '',
        streetNumber: (object.address as unknown as { streetNumber?: string | null })?.streetNumber ?? null,
        postalCode: object.address?.postalCode ?? '',
        city: object.address?.city ?? '',
        country: object.address?.country ?? '',
      });
      setExternalNotes(object.externalNotes || '');
      setInternalNotes(object.internalNotes || '');
      setGeo({
        latitude: object.latitude === null || object.latitude === undefined ? '' : String(object.latitude),
        longitude: object.longitude === null || object.longitude === undefined ? '' : String(object.longitude),
      });
    } else {
      // create mode defaults
      setName('');
      setStatus('Active');
      setBillingInterval(DEFAULT_BILLING_INTERVAL);
      setObjectType(null);
      setAddress({
        street: '',
        streetNumber: null,
        postalCode: '',
        city: '',
        country: '',
      });
      setExternalNotes('');
      setInternalNotes('');
      setGeo({ latitude: '', longitude: '' });
    }

    setGeoError(null);
    setGeoTouched(false);
  }, [isOpen, mode, object, customerId]);

  const geoParsed = useMemo(() => {
    const lat = parseDecimal(geo.latitude);
    const lon = parseDecimal(geo.longitude);
    return { lat, lon };
  }, [geo.latitude, geo.longitude]);

  const addressSearchQuery = useMemo(() => {
    const street = [address.street, (address as unknown as { streetNumber?: string | null }).streetNumber]
      .filter(Boolean)
      .join(' ');
    const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ');
    const country = address.country || '';

    const q = [street, cityLine, country].filter(Boolean).join(', ');
    return q;
  }, [address]);

  const validateGeo = () => {
    const latRaw = geo.latitude.trim();
    const lonRaw = geo.longitude.trim();

    const oneFilled = !!latRaw || !!lonRaw;
    const bothFilled = !!latRaw && !!lonRaw;

    if (oneFilled && !bothFilled) {
      return t('objects:validation.bothRequired');
    }

    if (!oneFilled) return null;

    if (geoParsed.lat === null || geoParsed.lon === null) {
      return t('objects:validation.invalidGeo');
    }

    if (geoParsed.lat < -90 || geoParsed.lat > 90) return t('objects:validation.latitudeRange');
    if (geoParsed.lon < -180 || geoParsed.lon > 180) return t('objects:validation.longitudeRange');

    return null;
  };

  const handleAddressResolved = (resolved: GeocodeAddress) => {
    setAddress((prev) => ({
      ...prev,
      street: resolved.street || prev.street,
      streetNumber: resolved.houseNumber || prev.streetNumber,
      postalCode: resolved.postalCode || prev.postalCode,
      city: resolved.city || prev.city,
      country: resolved.country || prev.country,
    }));
  };

  const setGeoFromMap = (value: { latitude: number; longitude: number } | null) => {
    setGeoTouched(true);
    setGeoError(null);

    if (!value) {
      setGeo({ latitude: '', longitude: '' });
      return;
    }

    setGeo({ latitude: String(value.latitude), longitude: String(value.longitude) });
  };

  const handleSubmit = async () => {
    const geoValidation = validateGeo();
    setGeoError(geoValidation);
    if (geoValidation) return;

    const effectiveCustomerId = selectedCustomerId || customerId;

    if (mode === 'create' && !effectiveCustomerId) {
      setGeoError(t('objects:validation.invalidCustomerId'));
      return;
    }

    setIsSubmitting(true);
    try {
      const base = {
        // why: name is required by Create/UpdateObjectRequest (non-nullable string).
        name: name || '',
        status: status || null,
        billingInterval,
        // null clears the type on update (full replace, ADR-0024).
        type: objectType,
        address: address as FacilityFlowCoreCommonDtosAddressDto,
        externalNotes: externalNotes || null,
        internalNotes: internalNotes || null,
      };

      if (mode === 'edit') {
        const payload: UpdateObjectRequestWithGeo = { ...base };

        // Backend rule: send lat/lon only when changing them; otherwise omit (leave unchanged).
        if (geoTouched) {
          const latRaw = geo.latitude.trim();
          const lonRaw = geo.longitude.trim();

          if (!latRaw && !lonRaw) {
            payload.latitude = null;
            payload.longitude = null;
          } else {
            payload.latitude = geoParsed.lat;
            payload.longitude = geoParsed.lon;
          }
        }

        await onSave(payload);
      } else {
        const payload: CreateObjectRequestWithGeo = {
          // why: guarded above — create mode returns early when this is empty, but TS cannot
          // correlate that check with this branch.
          customerId: effectiveCustomerId!,
          ...base,
        };

        // Create: when values filled, always include
        const latRaw = geo.latitude.trim();
        const lonRaw = geo.longitude.trim();
        if (latRaw && lonRaw) {
          payload.latitude = geoParsed.lat;
          payload.longitude = geoParsed.lon;
        }

        await onSave(payload);
      }

      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? t('objects:createObject') : t('objects:editObject')}
      size="lg"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={mode === 'create' ? t('common:actions.create') : t('common:actions.save')}
          cancelText={t('common:actions.cancel')}
          confirmDisabled={isSubmitting}
          loading={isSubmitting}
        />
      }
    >
      <div className="space-y-6">
        {showCustomerSelect && (
          <FormField label={t('objects:fields.customer')} required>
            <select
              value={selectedCustomerId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedCustomerId(e.target.value)}
              aria-label={t('objects:fields.customer')}
              className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('objects:placeholders.customer')}</option>
              {customers?.map((c) => (
                <option key={c.id ?? ''} value={c.id ?? ''}>
                  {c.name ?? '—'}
                </option>
              ))}
            </select>
          </FormField>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('objects:fields.name')} required>
            <Input value={name} onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)} placeholder={t('objects:placeholders.name')} />
          </FormField>

          <FormField label={t('objects:fields.status')}>
            <select
              value={status}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setStatus(e.target.value)}
              aria-label={t('objects:fields.status')}
              className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="Active">{t('common:status.active')}</option>
              <option value="Inactive">{t('common:status.inactive')}</option>
            </select>
          </FormField>

          <FormField label={t('objects:fields.billingInterval')} helperText={t('objects:hints.billingInterval')}>
            <select
              value={billingInterval}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setBillingInterval(toBillingInterval(e.target.value))}
              aria-label={t('objects:fields.billingInterval')}
              className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {OBJECT_BILLING_INTERVALS.map((interval) => (
                <option key={interval} value={interval}>
                  {t(`objects:billingMode.${interval}`)}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label={t('objects:fields.type')}>
            <select
              value={objectType ?? ''}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setObjectType(toObjectType(e.target.value))}
              aria-label={t('objects:fields.type')}
              className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('objects:objectType.none')}</option>
              {OBJECT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(objectTypeLabelKey(type))}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('objects:address.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('objects:fields.street')} required>
              <Input
                value={address.street || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAddress((prev: FacilityFlowCoreCommonDtosAddressDto) => ({ ...prev, street: e.target.value }))}
                placeholder={t('objects:placeholders.street')}
              />
            </FormField>

            <FormField label={t('objects:fields.streetNumber')}>
              <Input
                value={(address as unknown as { streetNumber?: string | null }).streetNumber || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setAddress((prev: FacilityFlowCoreCommonDtosAddressDto) => ({
                    ...prev,
                    streetNumber: e.target.value || null,
                  }))
                }
                placeholder={t('objects:placeholders.streetNumber')}
              />
            </FormField>

            <FormField label={t('objects:fields.postalCode')} required>
              <Input
                value={address.postalCode || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAddress((prev: FacilityFlowCoreCommonDtosAddressDto) => ({ ...prev, postalCode: e.target.value }))}
                placeholder={t('objects:placeholders.postalCode')}
              />
            </FormField>

            <FormField label={t('objects:fields.city')} required>
              <Input
                value={address.city || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAddress((prev: FacilityFlowCoreCommonDtosAddressDto) => ({ ...prev, city: e.target.value }))}
                placeholder={t('objects:placeholders.city')}
              />
            </FormField>

            <FormField label={t('objects:fields.country')}>
              <Input
                value={address.country || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAddress((prev: FacilityFlowCoreCommonDtosAddressDto) => ({ ...prev, country: e.target.value }))}
                placeholder={t('objects:placeholders.country')}
              />
            </FormField>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('objects:geolocation.title')}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('objects:geolocation.description')}
          </p>

          {geoError && (
            <div className="text-sm text-red-600">{geoError}</div>
          )}

          <MapPicker
            value={
              geoParsed.lat !== null && geoParsed.lon !== null
                ? { latitude: geoParsed.lat, longitude: geoParsed.lon }
                : null
            }
            onChange={setGeoFromMap}
            onAddressResolved={handleAddressResolved}
            disabled={isSubmitting}
            containerClassName="ff-map"
            showSearch
            searchQuery={addressSearchQuery}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('objects:fields.latitude')}>
              <Input value={geo.latitude} readOnly className="bg-gray-50 dark:bg-gray-800" />
            </FormField>
            <FormField label={t('objects:fields.longitude')}>
              <Input value={geo.longitude} readOnly className="bg-gray-50 dark:bg-gray-800" />
            </FormField>
          </div>

          {geoTouched && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {t('objects:geolocation.editNote')}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t('objects:notes.title')}</h3>
          <div className="grid grid-cols-1 gap-4">
            <FormField label={t('objects:fields.externalNotes')}>
              <textarea
                value={externalNotes}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setExternalNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('objects:placeholders.externalNotes')}
              />
            </FormField>

            <FormField label={t('objects:fields.internalNotes')}>
              <textarea
                value={internalNotes}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInternalNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('objects:placeholders.internalNotes')}
              />
            </FormField>
          </div>
        </div>
      </div>
    </SlideOver>
  );
}
